import { useCallback, useEffect, useRef, useState } from 'react'
import { matchPath, useHistory, useLocation } from 'react-router-dom'
import { useErrorService, useUserService } from '@abroad/components'
import API from '../../utils/API'
import { isQuestPaymentSatisfied } from './questPaymentUtils'

export const EXPERIENCE_PAYMENT_REQUIRED_MESSAGE =
  'Payment required to access these features.'

/** Same delay as RestClient plan paywall (`paymentRequired` → /user-payment) */
const REDIRECT_DELAY_MS = 500

export const EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY =
  'experience_payment_lock_quest_id'

/**
 * Allowed while enrollment is done but deposit/full payment is pending.
 * Same idea as staying on /user-payment for growth.
 */
const ALLOWED_PATHS = [
  '/your-experiences/:questId/payments',
  '/your-experiences/:questId/payment-status',
  '/your-experiences/:questId/typeform/:questCustomFormId',
  '/your-experiences/:questId/typeform/:questCustomFormId/answer',
]

/**
 * Only Experiences sidebar routes are blocked when payment is pending.
 * Home, Assessments, Coaching, and Learning stay accessible.
 */
const RESTRICTED_PATH_PREFIXES = [
  '/experiences',
  '/your-experiences',
  '/quests/',
]

const isAllowedPath = (pathname) =>
  ALLOWED_PATHS.some((path) =>
    matchPath(pathname, { path, exact: true, strict: false }),
  )

const isRestrictedPath = (pathname) => {
  if (isAllowedPath(pathname)) return false

  return RESTRICTED_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) {
      return pathname.startsWith(prefix)
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}

const isPaymentStatusPath = (pathname) =>
  !!matchPath(pathname, {
    path: '/your-experiences/:questId/payment-status',
    exact: true,
  })

const readStoredLock = () => {
  try {
    return sessionStorage.getItem(EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY)
  } catch (e) {
    return null
  }
}

const writeStoredLock = (questId) => {
  try {
    if (questId) {
      sessionStorage.setItem(EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY, questId)
    } else {
      sessionStorage.removeItem(EXPERIENCE_PAYMENT_LOCK_STORAGE_KEY)
    }
  } catch (e) {
    // ignore storage errors
  }
}

const ExperiencePaymentLock = () => {
  const { user } = useUserService()
  const userId = user?._id
  const location = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const [lockedQuestId, setLockedQuestId] = useState(readStoredLock)
  // Don't redirect until payment status has been freshly resolved
  const [isLockReady, setIsLockReady] = useState(false)
  const resolvingRef = useRef(false)
  const lastHandledPathRef = useRef(null)

  const setLock = useCallback((questId) => {
    writeStoredLock(questId)
    setLockedQuestId(questId)
  }, [])

  const resolveUnpaidQuest = useCallback(async () => {
    if (!userId || resolvingRef.current) return

    resolvingRef.current = true
    setIsLockReady(false)
    try {
      const { data: quests } = await API.quests.allQuest()
      let unpaidId = null

      for (const quest of quests || []) {
        try {
          const { data } = await API.quests.getQuestsSteps(quest._id)
          const questPayments = data?.questPayments || {}
          const paymentMade = isQuestPaymentSatisfied({
            canPayFull: questPayments.canPayFull,
            canPayDeposit: questPayments.canPayDeposit,
            canPayRemaining: questPayments.canPayRemaining,
            depositAmount: questPayments.depositAmount,
            actualCost: questPayments.actualCost,
            discountedCost: questPayments.discountedCost,
          })

          if (data?.onboardingCompleted && !paymentMade) {
            unpaidId = quest._id
            break
          }
        } catch (e) {
          // Skip quests that error; continue searching
        }
      }

      setLock(unpaidId)
    } catch (e) {
      // Keep existing lock if resolve fails
    } finally {
      resolvingRef.current = false
      setIsLockReady(true)
    }
  }, [userId, setLock])

  useEffect(() => {
    if (!userId) {
      setLock(null)
      setIsLockReady(false)
      lastHandledPathRef.current = null
      return
    }
    resolveUnpaidQuest()
  }, [userId, resolveUnpaidQuest, setLock])

  // After payment / on experiences home: clear stale lock, then re-check
  useEffect(() => {
    if (!userId) return

    const onPaymentStatus = isPaymentStatusPath(location.pathname)
    const onExperiencesHome = location.pathname === '/your-experiences'

    if (onPaymentStatus) {
      // Prevent bounce-back to payment while Stripe status is settling
      setLock(null)
      setIsLockReady(false)
      lastHandledPathRef.current = null
      return
    }

    if (onExperiencesHome) {
      // Re-resolve after payment success redirect to home
      resolveUnpaidQuest()
    }
  }, [location.pathname, userId, resolveUnpaidQuest, setLock])

  // Toast + redirect only when unpaid user opens an Experiences page
  useEffect(() => {
    if (!isLockReady || !lockedQuestId || !userId) return
    if (!isRestrictedPath(location.pathname)) {
      lastHandledPathRef.current = null
      return
    }

    const paymentPath = `/your-experiences/${lockedQuestId}/payments`
    if (location.pathname === paymentPath) return

    // Toast only once per blocked path (login landing or sidebar navigation)
    if (lastHandledPathRef.current !== location.pathname) {
      lastHandledPathRef.current = location.pathname
      Error.showError({
        code: 'payment_required',
        message: EXPERIENCE_PAYMENT_REQUIRED_MESSAGE,
      })
    }

    const timer = setTimeout(() => {
      history.replace(paymentPath)
    }, REDIRECT_DELAY_MS)

    return () => clearTimeout(timer)
  }, [isLockReady, lockedQuestId, location.pathname, history, Error, userId])

  return null
}

export default ExperiencePaymentLock
